import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { DogIcon as Dog, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, ShareIcon as Share, CheckCircleIcon as CheckCircle, TreeStructureIcon as TreeStructure, UsersFourIcon as UsersFour, TrophyIcon as Trophy } from "@phosphor-icons/react";
import { photoUrl, thumbUrl, formatDate } from "@/lib/utils-app";
import { toast } from "sonner";
import HealthFilesCard from "@/components/HealthFilesCard";
import PhotoLightbox from "@/components/PhotoLightbox";
import MicrochipProtectedBadge from "@/components/MicrochipProtectedBadge";
import BookVideoTourDialog from "@/components/BookVideoTourDialog";
import ReportButton from "@/components/ReportButton";
import { toYouTubeEmbed, toVimeoEmbed } from "@/components/DogVideoUploader";
import { useAuth } from "@/contexts/AuthContext";
import { VideoCameraIcon as VideoCamera } from "@phosphor-icons/react";

export default function PublicDog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dog, setDog] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get(`/public/dogs/${id}`),
      api.get(`/public/dogs/${id}/pedigree?generations=3`),
    ])
      .then(([d, p]) => {
        setDog(d.data);
        setTree(p.data);
        document.title = `${d.data.name} · Armstrong K9 Registry`;
        // If we landed via a UUID (or stale slug), 301-equivalent to the
        // canonical slug URL. Keeps social shares / bookmarks working but
        // funnels SEO juice to one URL.
        const canonical = d.data.slug;
        if (canonical && canonical !== id) {
          navigate(`/d/${canonical}`, { replace: true });
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => {
      document.title = "Armstrong K9 Registry";
    };
  }, [id, navigate]);

  const share = async () => {
    // Use the OG-rich backend share URL so when this link is pasted to
    // Facebook / iMessage / Slack, the 1200×630 share-card preview
    // appears automatically. Backend serves the OG HTML + redirects
    // human browsers straight to the SPA listing page.
    const slugOrId = dog?.slug || id;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/dogs/${slugOrId}/share`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: dog?.name || "Dog",
          url,
        });
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — link preview will auto-render on social.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="text-[#6B6B6B]">Loading…</div>
      </PublicShell>
    );
  }
  if (notFound || !dog) {
    return (
      <PublicShell>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-12 text-center">
          <Dog size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
          <p className="text-[#6B6B6B]">Dog profile not found.</p>
        </div>
      </PublicShell>
    );
  }

  const titles = dog.titles || [];
  const awards = dog.awards || [];
  const hasPhoto = Boolean(dog.photos?.[0]);
  const canonicalUrl = `${webOrigin()}/d/${dog.slug || dog.id}`;
  const ogImage = dog.photos?.[0]
    ? `${webOrigin()}/api/files/${dog.photos[0]}`
    : `${webOrigin()}/logo-512.png`;
  const kennelName = dog.breeder?.kennel_name || dog.breeder?.name || "";
  const productJsonLd = dog.for_sale && dog.asking_price ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": dog.name,
    "description": dog.sale_description || `${dog.breed} ${dog.sex || ""}`.trim(),
    "image": ogImage,
    "category": dog.breed,
    "sku": dog.registration_number || dog.id,
    "url": canonicalUrl,
    ...(kennelName && {
      "brand": { "@type": "Brand", "name": kennelName },
    }),
    "offers": {
      "@type": "Offer",
      "price": dog.asking_price,
      "priceCurrency": dog.sale_currency || "USD",
      "availability": dog.sale_status === "available"
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "url": canonicalUrl,
      ...(kennelName && {
        "seller": { "@type": "Organization", "name": kennelName },
      }),
    },
  } : {
    "@context": "https://schema.org",
    "@type": "Thing",
    "name": dog.name,
    "description": `${dog.breed} ${dog.sex || ""}${dog.dob ? `, born ${dog.dob}` : ""}`.trim(),
    "url": canonicalUrl,
  };

  return (
    <PublicShell>
      <SeoHead
        title={`${dog.name} · ${dog.breed} · Armstrong K9 Registry`}
        description={`${dog.name} — ${dog.breed} ${dog.sex || ""}${dog.dob ? `, born ${dog.dob}` : ""}${dog.for_sale && dog.asking_price ? `. Asking $${dog.asking_price}` : ""}. View pedigree and details on Armstrong K9 Registry.`.slice(0, 250)}
        imageUrl={ogImage}
        url={canonicalUrl}
        type={dog.for_sale ? "product" : "profile"}
        jsonLd={productJsonLd}
        breadcrumbs={[
          { name: "Home", url: `${webOrigin()}/` },
          { name: "Marketplace", url: `${webOrigin()}/marketplace` },
          { name: dog.breed || "Dog", url: `${webOrigin()}/marketplace?breed=${encodeURIComponent(dog.breed || "")}` },
          { name: dog.name, url: canonicalUrl },
        ]}
      />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
        {/* Portrait */}
        <aside className="md:col-span-5" data-testid="public-dog-portrait">
          <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="aspect-[4/5] bg-[#FAFAFA]">
              {hasPhoto ? (
                <button
                  type="button"
                  onClick={() => setLightboxIndex(0)}
                  className="block w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  data-testid="public-dog-hero-photo"
                  aria-label="Open photo gallery"
                >
                  <img src={photoUrl(dog.photos[0])} alt={dog.name} className="w-full h-full object-cover" />
                </button>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Dog size={72} weight="duotone" className="text-[#6B6B6B]/40" />
                </div>
              )}
            </div>
            {dog.photos?.length > 1 && (
              <div className="grid grid-cols-4 gap-2 p-3">
                {dog.photos.slice(1, 5).map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setLightboxIndex(i + 1)}
                    data-testid={`public-dog-thumb-${i + 1}`}
                    className="aspect-square rounded-md overflow-hidden bg-[#FAFAFA] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold hover:opacity-90 transition-opacity"
                    aria-label={`Open photo ${i + 2}`}
                  >
                    <img
                      src={thumbUrl(p) || photoUrl(p)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = photoUrl(p); }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Info */}
        <div className="md:col-span-7">
          <div className="flex items-center gap-2 mb-2">
            {dog.sex === "male" ? (
              <GenderMale size={20} className="text-ink" />
            ) : (
              <GenderFemale size={20} className="text-crimson" />
            )}
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              {dog.breed}
            </span>
          </div>
          <h1
            className="font-spectral text-4xl sm:text-5xl tracking-tight"
            data-testid="public-dog-name"
          >
            {dog.name}
          </h1>

          {dog.microchip_protected !== undefined && (
            <div className="mt-3">
              <MicrochipProtectedBadge
                protected={!!dog.microchip_protected}
                last4={dog.microchip_last4}
                size="md"
              />
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 mt-6 text-sm">
            {dog.dob && (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Born
                </dt>
                <dd className="text-ink mt-0.5">{formatDate(dog.dob)}</dd>
              </div>
            )}
            {dog.color && (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Color
                </dt>
                <dd className="text-ink mt-0.5">{dog.color}</dd>
              </div>
            )}
            {dog.registration_number && (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Registration
                </dt>
                <dd className="text-ink mt-0.5 font-mono text-xs">
                  {dog.registration_number}
                </dd>
              </div>
            )}
            {dog.breeder && (
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                  Kennel
                </dt>
                <dd className="text-ink mt-0.5">
                  <Link
                    to={`/k/${dog.breeder.id}`}
                    className="hover:text-crimson inline-flex items-center gap-1"
                    data-testid="public-dog-breeder-link"
                  >
                    <UsersFour size={14} weight="duotone" />
                    {dog.breeder.kennel_name || dog.breeder.name}
                  </Link>
                </dd>
              </div>
            )}
          </dl>

          {titles.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
                Titles
              </p>
              <div className="flex flex-wrap gap-1.5">
                {titles.map((t) => (
                  <span
                    key={t.id}
                    className="bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-3 py-1 text-xs font-semibold"
                  >
                    {t.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {awards.length > 0 && (
            <div className="mt-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
                Awards
              </p>
              <ul className="space-y-1.5">
                {awards.slice(0, 6).map((a) => (
                  <li key={a.id} className="flex items-baseline gap-2 text-sm">
                    <Trophy size={14} weight="duotone" className="text-crimson flex-shrink-0" />
                    <span className="font-medium">{a.name}</span>
                    <span className="text-[#6B6B6B] text-xs">
                      {a.event}
                      {a.placement && ` · ${a.placement}`}
                      {a.date && ` · ${formatDate(a.date)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={share}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="public-dog-share-btn"
            >
              {copied ? (
                <>
                  <CheckCircle size={16} className="mr-1" weight="fill" /> Copied
                </>
              ) : (
                <>
                  <Share size={16} className="mr-1" /> Share profile
                </>
              )}
            </Button>
            <Button
              onClick={() => {
                if (!user) {
                  // Send signed-out visitors to register so they have an
                  // account to receive the tour confirmation email on.
                  window.location.href = `/register?next=/d/${dog.id}`;
                  return;
                }
                setTourOpen(true);
              }}
              variant="outline"
              className="rounded-full border-[#0E0E0E] text-ink hover:bg-[#0E0E0E] hover:text-white"
              data-testid="public-dog-book-tour-btn"
            >
              <VideoCamera size={16} className="mr-1" weight="duotone" />
              Book video tour · $35
            </Button>
            <ReportButton
              targetType="dog"
              targetId={dog.slug || dog.id}
              targetLabel={dog.name}
              testIdPrefix="public-dog-report"
            />
          </div>

          {/* Health files — public summary + Buyer-Pass-gated detail */}
          <div className="mt-6">
            <HealthFilesCard dogId={dog.id} summary={dog.health_summary} />
          </div>
        </div>
      </div>

      <BookVideoTourDialog
        open={tourOpen}
        onOpenChange={setTourOpen}
        dogId={dog?.id}
        dogName={dog?.name}
      />

      {/* Working videos — short clips of the dog in action (bitework /
          hunt / agility / obedience). Owners upload up to 3 via DogForm. */}
      {Array.isArray(dog.videos) && dog.videos.length > 0 && (
        <div
          className="bg-white border border-[#E5E5E5] rounded-xl p-6 mb-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          data-testid="public-dog-videos"
        >
          <div className="flex items-center gap-2 mb-5">
            <VideoCamera size={20} weight="duotone" className="text-crimson" />
            <h2 className="font-spectral text-2xl tracking-tight">
              {dog.name} in action
            </h2>
            <span className="text-xs text-[#6B6B6B] ml-1">
              {dog.videos.length} {dog.videos.length === 1 ? "clip" : "clips"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dog.videos.map((v) => (
              <PublicVideoTile key={v.id} video={v} dogId={dog.id} dogName={dog.name} />
            ))}
          </div>
          {/* Platform-shield disclaimer — ToS §7a. Keep it light but
              present so a buyer can't later claim Armstrong K9 Registry
              represented training, temperament, or fitness. */}
          <p className="mt-3 text-[11px] text-[#6B6B6B] italic leading-snug">
            Sport / working footage submitted by the seller. Not a Registry
            assessment of training, temperament, certifications, or fitness for
            any purpose.{" "}
            <Link to="/legal/terms#working-video" className="underline hover:text-ink">
              Terms §7a
            </Link>
            .
          </p>
        </div>
      )}

      {/* Pedigree */}
      {tree && (tree.sire || tree.dam) && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 mb-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 mb-5">
            <TreeStructure size={20} weight="duotone" className="text-crimson" />
            <h2 className="font-spectral text-2xl tracking-tight">Pedigree</h2>
            <span className="text-xs text-[#6B6B6B] ml-1">3 generations</span>
          </div>
          <PedigreeColumns root={tree} />
        </div>
      )}

      <div className="border-t border-[#E5E5E5] pt-8 text-center">
        <p className="text-sm text-[#6B6B6B]">
          Want a profile like this for your dog?{" "}
          <Link
            to="/register"
            className="text-ink font-semibold hover:text-crimson"
            data-testid="public-dog-signup-cta"
          >
            Create a free account →
          </Link>
        </p>
      </div>
      <PhotoLightbox
        photos={dog?.photos || []}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onChange={setLightboxIndex}
      />
    </PublicShell>
  );
}

/** Single public-facing video tile — handles file <video>, YouTube and Vimeo. */
function PublicVideoTile({ video, dogId, dogName }) {
  const isFile = video.kind === "file";
  const isYouTube = video.kind === "youtube";
  const isVimeo = video.kind === "vimeo";
  return (
    <div
      className="rounded-lg overflow-hidden border border-[#E5E5E5] bg-white"
      data-testid={`public-dog-video-${video.id}`}
    >
      <div className="aspect-video bg-black">
        {isFile && (
          <video
            src={video.url}
            poster={video.poster_url || undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        )}
        {isYouTube && (
          <iframe
            src={toYouTubeEmbed(video.url)}
            title={video.title || "Working footage"}
            allow="accelerometer; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            loading="lazy"
          />
        )}
        {isVimeo && (
          <iframe
            src={toVimeoEmbed(video.url)}
            title={video.title || "Working footage"}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            loading="lazy"
          />
        )}
      </div>
      {(video.title || video.caption) && (
        <div className="px-3 py-2">
          {video.title && (
            <p className="text-[13px] font-semibold text-ink leading-tight truncate">
              {video.title}
            </p>
          )}
          {video.caption && (
            <p className="text-[12px] text-[#6B6B6B] leading-snug">{video.caption}</p>
          )}
        </div>
      )}
      {/* Per-clip flag — opens the existing ReportButton dialog wired
          to `target_subtype="video"` so admin queue can take action on
          this specific clip without nuking the whole dog listing. */}
      <div className="flex justify-end px-2 pb-1 -mt-1">
        <ReportButton
          targetType="dog"
          targetId={dogId}
          targetSubtype="video"
          targetSubid={video.id}
          targetLabel={`${dogName || "this dog"} — ${video.title || video.caption || "working video"}`}
          testIdPrefix={`report-video-${video.id}`}
          buttonLabel=""
          iconOnly
          className="!h-7 !w-7 !p-0 opacity-50 hover:opacity-100"
        />
      </div>
    </div>
  );
}

/** 3-column grid pedigree: parents | grandparents | great-grandparents */
function PedigreeColumns({ root }) {
  const cols = [1, 2, 3].map((depth) => columnNodes(root, depth));
  const labels = ["Parents", "Grandparents", "Great-grandparents"];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="public-dog-pedigree">
      {cols.map((nodes, i) => (
        <div key={i}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-3">
            {labels[i]}
          </p>
          <div className="space-y-2">
            {nodes.map((n, idx) => (
              <PedigreeNode key={idx} node={n} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function columnNodes(root, depth) {
  // Returns 2^depth entries in sire-first order at the given depth.
  const out = [];
  const walk = (node, d, target) => {
    if (d === target) {
      out.push(node);
      return;
    }
    if (!node) {
      walk(null, d + 1, target);
      walk(null, d + 1, target);
      return;
    }
    walk(node.sire, d + 1, target);
    walk(node.dam, d + 1, target);
  };
  walk(root, 0, depth);
  return out;
}

function PedigreeNode({ node }) {
  if (!node) {
    return (
      <div className="border border-dashed border-[#E5E5E5] rounded-lg p-2 text-[#6B6B6B] text-xs italic h-14 flex items-center">
        Unknown
      </div>
    );
  }
  return (
    <Link
      to={`/d/${node.slug || node.id}`}
      className="block border border-[#E5E5E5] rounded-lg p-2 hover:border-[#C1272D]/40 hover:bg-[#C1272D]/5 transition-colors h-14 flex flex-col justify-center"
      data-testid={`ped-node-${node.id}`}
    >
      <div className="text-sm font-medium truncate">{node.name}</div>
      <div className="text-[10px] text-[#6B6B6B] truncate">
        {(node.titles || []).slice(0, 2).join(", ") || node.breed}
      </div>
    </Link>
  );
}
